import type { WorkflowEditView, WorkflowView } from '@/lib/api/generated/model';

import { fromEditView, fromWorkflowView } from './fromView';
import type { WorkflowDraft } from './types';

/**
 * The draft, its undo stack, and the actions that move between them.
 *
 * Undo granularity is the design decision here (research D2): a history entry
 * is one *intention*, not one keystroke and not one animation frame. Typing a
 * prompt is one entry committed on blur; dragging a node is one entry committed
 * on drop. Anything else makes undo useless — fifty presses to walk back one
 * sentence, or one press that rewinds a drag by three pixels.
 */

export type DraftHistory = {
  present: WorkflowDraft;
  /** Most recent last. Capped at `HISTORY_LIMIT`; the oldest is dropped. */
  past: WorkflowDraft[];
  future: WorkflowDraft[];
  /**
   * The draft as it stood before the current gesture began.
   *
   * Set by the first transient edit after a commit, consumed by the next
   * commit, and the reason one undo returns a dragged node to where it started
   * rather than to the last pointer frame. Without it, `commit` would push
   * whatever transient value happened to be current — which is a position the
   * user never chose and never saw settle.
   */
  gestureBaseline?: WorkflowDraft;
};

/**
 * How far back undo reaches.
 *
 * A draft is a whole document and each entry is a full copy, so this is a
 * memory bound as much as a usability one. Fifty edits is far past what anyone
 * walks back by hand, and well short of anything that matters for a document
 * this size.
 */
export const HISTORY_LIMIT = 50;

/**
 * What the builder is opened on, or what a write returned: a workflow, or a
 * published workflow with its pending changes (spec 144).
 */
export type DraftSource = WorkflowView | WorkflowEditView;

export type DraftAction =
  /** Adopt the document a successful save returned (FR-051). */
  | { type: 'SAVE_SUCCEEDED'; view: DraftSource }
  | { type: 'UNDO' }
  | { type: 'REDO' }
  /**
   * One edit, recorded as one history entry.
   *
   * Carries a function of the current draft rather than a finished draft, so a
   * caller cannot build on a stale copy it closed over — with a canvas and an
   * inspector both editing, that staleness is a real and silent way to lose a
   * change. React reducers may carry functions; the constraint people remember
   * is Redux's serialisability rule, which does not apply here.
   */
  | { type: 'EDIT'; change: (draft: WorkflowDraft) => WorkflowDraft }
  /** The same, without recording history — the frames of a gesture (D2). */
  | { type: 'EDIT_TRANSIENT'; change: (draft: WorkflowDraft) => WorkflowDraft };

export function initialHistory(source: DraftSource): DraftHistory {
  return {
    present:
      'workflow' in source ? fromEditView(source) : fromWorkflowView(source),
    past: [],
    future: [],
  };
}

export function draftReducer(
  state: DraftHistory,
  action: DraftAction,
): DraftHistory {
  switch (action.type) {
    case 'SAVE_SUCCEEDED': {
      // The server's response becomes the new baseline: it carries the version
      // the next save must quote, and it is authoritative about anything the
      // service normalised. History is cleared because undoing past a save
      // would offer to restore a state the server no longer agrees with —
      // the draft would look editable while every save conflicted (FR-051).
      return initialHistory(action.view);
    }

    case 'UNDO': {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        present: previous,
        past: state.past.slice(0, -1),
        future: [state.present, ...state.future],
        gestureBaseline: undefined,
      };
    }

    case 'REDO': {
      const [next, ...rest] = state.future;
      if (!next) return state;
      return {
        present: next,
        past: [...state.past, state.present],
        future: rest,
        gestureBaseline: undefined,
      };
    }

    case 'EDIT':
      return commit(state, action.change(state.present));

    case 'EDIT_TRANSIENT':
      return replace(state, action.change(state.present));
  }
}

/**
 * Record an edit: the new present, the old one pushed onto the past.
 *
 * The future is dropped, which is the standard and the right behaviour —
 * editing after undoing forks the history, and keeping the abandoned branch
 * would make redo restore something the user has already replaced.
 *
 * Every edit sets `dirty`. That flag drives the save indicator (FR-045) and the
 * navigation guard (FR-046), and the reason it is set here rather than by each
 * action is that forgetting it in one action is a silent data-loss bug.
 */
export function commit(state: DraftHistory, next: WorkflowDraft): DraftHistory {
  // The entry recorded is where the *user* last was, which after a gesture is
  // the pre-gesture snapshot rather than the current present.
  const entry = state.gestureBaseline ?? state.present;
  const past = [...state.past, entry];
  return {
    present: { ...next, dirty: true },
    past:
      past.length > HISTORY_LIMIT
        ? past.slice(past.length - HISTORY_LIMIT)
        : past,
    future: [],
    gestureBaseline: undefined,
  };
}

/**
 * Change the present without recording history.
 *
 * For the intermediate frames of a continuous gesture — a node being dragged has
 * a new position on every pointer move, and none of them is an intention. The
 * gesture's final state is committed with `commit`, pushing the *pre-gesture*
 * snapshot so one undo returns the node to where it started (D2).
 */
export function replace(
  state: DraftHistory,
  next: WorkflowDraft,
): DraftHistory {
  return {
    ...state,
    present: { ...next, dirty: true },
    // Only the *first* frame of a gesture captures the baseline; the rest are
    // just as transient and must not overwrite it.
    gestureBaseline: state.gestureBaseline ?? state.present,
  };
}

export function canUndo(state: DraftHistory): boolean {
  return state.past.length > 0;
}

export function canRedo(state: DraftHistory): boolean {
  return state.future.length > 0;
}
